/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionItemTypeRegistry } from '../../registry/Base.type';
import {
  ActionManager,
  ActionManagerFn,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { actionNames, defaultValue } from './ActionGoToRecord';

const ActionGoToRecordManager =
  <
    K extends keyof Pick<
      ActionItemTypeRegistry,
      | 'ActionGoToNextRecord'
      | 'ActionGoToPreviousRecord'
      | 'ActionGoToFirstRecord'
      | 'ActionGoToLastRecord'
    >,
  >(
    type: K,
  ): ActionManagerFn<K> =>
  (platform: Platform): ActionManager<K> => {
    return {
      ...baseActionManager(platform, type),
      defaultValue: defaultValue as Required<ActionItemTypeRegistry[K]>,
      props: {
        ...baseActionProps,
        alias: {
          label: 'Alias',
          type: 'DataSourceAliasChooser',
          allowedTypes: [
            'CloudDataStore',
            'DataStore',
            'LocalDataStore',
            'ReadOnlyDataStore',
            'TransientDataStore',
          ],
          required: true,
        },
      } as PropertyDefinitions<ActionItemTypeRegistry[K]>,
      isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
      name: actionNames[type] ?? type,
      getDisplayText: (item) => item.props.alias,
      description: `**Overview**<br/>
The Go To Record actions represent a family of navigation components designed to traverse the dataset of a specific Data Store. This single manager is responsible for generating the logic for four distinct actions: **Go To Next Record**, **Go To Previous Record**, **Go To First Record**, and **Go To Last Record**.<br/><br/>
These components function as cursors for data stores, allowing the application to programmatically change the "active" or "current" record pointer without user selection. They are implemented generically to ensure consistent behavior across all traversal directions and support a wide range of data store types, including transient and read-only stores.

**Key Features**<br/>
* **Suite of Actions:** Dynamically generates the configuration for four specific movement types based on the input key: Next, Previous, First, and Last.
* **Broad Store Compatibility:** Supports interaction with CloudDataStore, DataStore, LocalDataStore, ReadOnlyDataStore, and TransientDataStore.
* **Dependency Tracking:** Implements dependency checks to properly register the link between the navigation action and the target Data Store alias.
* **Registry Visibility:** The editor displays the target alias in the action list, making it clear which data store is being navigated.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property that identifies the Data Store to navigate.
  * *Function:* The action will move the current record pointer of this specific store based on the action type (e.g., advancing to the next index).

**Use Cases**<br/>
* **Detail View Navigation:** "Next" and "Previous" buttons on a record detail page (e.g., an Employee Profile) to flip through records without returning to the main grid.
* **Custom Pagination:** Creating custom "First" and "Last" toolbar controls for a list view or carousel component.`,
    };
  };

export default ActionGoToRecordManager;
